import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import IntakePage from "@/pages/IntakePage";
import PrivacyVaultPage from "@/pages/PrivacyVaultPage";
import BusinessBrainPage from "@/pages/BusinessBrainPage";
import DashboardPage from "@/pages/DashboardPage";
import GhostLedgerPage from "@/pages/GhostLedgerPage";
import WeeklyReportPage from "@/pages/WeeklyReportPage";
import WeeklyPlanPage from "@/pages/WeeklyPlanPage";
import WalletPage from "@/pages/WalletPage";
import MarketRadarPage from "@/pages/MarketRadarPage";
import CompetitorSnapshotsPage from "@/pages/CompetitorSnapshotsPage";
import VideoPage from "@/pages/VideoPage";
import CeoDashboardPage from "@/pages/CeoDashboardPage";
import SeoOverviewPage from "@/pages/SeoOverviewPage";
import SocialSuitePage from "@/pages/SocialSuitePage";
import PublishQueuePage from "@/pages/PublishQueuePage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import SiteAuditPage from "@/pages/SiteAuditPage";
import PaginaFocusPage from "@/pages/PaginaFocusPage";
import MerkstemPage from "@/pages/MerkstemPage";
import AdviezenPage from "@/pages/AdviezenPage";
import EffectPage from "@/pages/EffectPage";
import VlogsPage from "@/pages/VlogsPage";
import BerichtenPage from "@/pages/BerichtenPage";
import DesignSystemPage from "@/pages/DesignSystemPage";
import NotFoundPage from "@/pages/NotFoundPage";
import PublicHomePage from "@/pages/PublicHomePage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import VoorwaardenPage from "@/pages/VoorwaardenPage";
import OverzichtPage from "@/pages/OverzichtPage";
import MijnWeekplanPage from "@/pages/MijnWeekplanPage";
import {
  ContentHubPage, KanalenPage, ResultatenPage, MarktEnConcurrentenPage,
  InstellingenPage, AdviseurPage,
} from "@/pages/wrappers";
import { FeatureGate } from "@/components/FeatureGate";
import "@/App.css";

// M145 — helper voor redirects die tab-parameter toevoegen én bestaande
// query-params (page, advice, ...) behouden.
function RedirectWithTab({ to, tab }) {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  if (tab) params.set("tab", tab);
  return <Navigate to={`${to}?${params.toString()}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <Routes>
          {/* Auth & onboarding */}
          <Route path="/login" element={<LoginPage mode="login" />} />
          <Route path="/signup" element={<LoginPage mode="signup" />} />
          <Route path="/wachtwoord-vergeten" element={<ForgotPasswordPage />} />
          <Route path="/wachtwoord-herstellen" element={<ResetPasswordPage />} />
          <Route path="/intake" element={<ProtectedRoute><IntakePage /></ProtectedRoute>} />

          {/* M149 — Publieke routes voor Google Verification: homepage,
              privacy-beleid, voorwaarden. Bereikbaar zonder inlog. */}
          <Route path="/home" element={<PublicHomePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/voorwaarden" element={<VoorwaardenPage />} />

          {/* M145 — Nieuwe primaire routes */}
          <Route path="/" element={<ProtectedRoute><OverzichtPage /></ProtectedRoute>} />
          <Route path="/mijn-weekplan" element={<ProtectedRoute requireIntake><MijnWeekplanPage /></ProtectedRoute>} />
          <Route path="/content" element={<ProtectedRoute requireIntake><ContentHubPage /></ProtectedRoute>} />
          <Route path="/kanalen" element={<ProtectedRoute requireIntake><KanalenPage /></ProtectedRoute>} />
          <Route path="/resultaten" element={<ProtectedRoute requireIntake><ResultatenPage /></ProtectedRoute>} />
          <Route path="/markt-en-concurrenten" element={<ProtectedRoute requireIntake><MarktEnConcurrentenPage /></ProtectedRoute>} />
          <Route path="/instellingen" element={<ProtectedRoute requireIntake><InstellingenPage /></ProtectedRoute>} />
          <Route path="/instellingen/bedrijfsprofiel" element={<RedirectWithTab to="/instellingen" tab="bedrijfsprofiel" />} />
          <Route path="/adviseur" element={<ProtectedRoute requireIntake><AdviseurPage /></ProtectedRoute>} />

          {/* M145 — Redirects van oude paden. Query-params blijven behouden. */}
          <Route path="/adviezen"      element={<RedirectWithTab to="/content" tab="adviezen" />} />
          <Route path="/pagina-focus"  element={<RedirectWithTab to="/content" tab="pagina-focus" />} />
          <Route path="/merkstem"      element={<RedirectWithTab to="/content" tab="merkstem" />} />
          <Route path="/vlogs"         element={<RedirectWithTab to="/content" tab="vlogs" />} />
          <Route path="/berichten"     element={<RedirectWithTab to="/content" tab="berichten" />} />
          <Route path="/integraties"   element={<Navigate to="/kanalen" replace />} />
          <Route path="/effect"        element={<RedirectWithTab to="/resultaten" tab="effect" />} />
          <Route path="/seo"           element={<RedirectWithTab to="/resultaten" tab="seo" />} />
          <Route path="/market-radar"  element={<RedirectWithTab to="/markt-en-concurrenten" tab="signalen" />} />
          <Route path="/site-audit"    element={<RedirectWithTab to="/markt-en-concurrenten" tab="audit" />} />
          <Route path="/weekplan"      element={<Navigate to="/mijn-weekplan" replace />} />
          <Route path="/weekly-report" element={<RedirectWithTab to="/mijn-weekplan" tab="vorige-week" />} />
          <Route path="/business-brain" element={<RedirectWithTab to="/instellingen" tab="bedrijfsprofiel" />} />
          <Route path="/privacy"       element={<RedirectWithTab to="/instellingen" tab="privacy" />} />
          <Route path="/wallet"        element={<RedirectWithTab to="/instellingen" tab="budget" />} />

          {/* Backward-compat: dashboard live nog even naast overzicht. */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />

          {/* Legacy achter feature-flags. Direct-URL zonder flag → 404. */}
          <Route path="/ceo"           element={<ProtectedRoute requireIntake><FeatureGate flag="feature_ceo_legacy_ui"><CeoDashboardPage /></FeatureGate></ProtectedRoute>} />
          <Route path="/vlogscripts"   element={<ProtectedRoute requireIntake><FeatureGate flag="feature_vlogscripts_ui"><VideoPage /></FeatureGate></ProtectedRoute>} />
          <Route path="/social"        element={<ProtectedRoute requireIntake><FeatureGate flag="feature_social_ui"><SocialSuitePage /></FeatureGate></ProtectedRoute>} />
          <Route path="/publish-queue" element={<ProtectedRoute requireIntake><FeatureGate flag="feature_publish_queue_ui"><PublishQueuePage /></FeatureGate></ProtectedRoute>} />
          <Route path="/ghost-ledger"  element={<ProtectedRoute requireIntake><FeatureGate flag="feature_ghost_ledger_ui"><GhostLedgerPage /></FeatureGate></ProtectedRoute>} />

          {/* Detail-routes die geen wrapper hebben. */}
          <Route path="/competitors/:id/snapshots" element={<ProtectedRoute requireIntake><CompetitorSnapshotsPage /></ProtectedRoute>} />

          {/* Dev / design-system */}
          <Route path="/dev/design-system" element={<DesignSystemPage />} />

          {/* M147.1 — Meta/TikTok/LinkedIn zijn per M143 uit de UI verwijderd.
              ConnectionsPage-component blijft in codebase (geparkeerd) maar
              is NIET meer bereikbaar via routes. Directe URLs vallen door
              naar de 404-catch-all. GSC/GA4-koppelingen starten nu direct
              vanaf /kanalen via /api/auth/google/services/start. */}

          {/* 404 catch-all */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
